import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import JSONEditor from "jsoneditor";
import "jsoneditor/dist/jsoneditor.min.css"; // Import the CSS
import { Box, Button, Typography, TextField, debounce, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faTimes } from "@fortawesome/free-solid-svg-icons";
import EasyJSONSchema from 'easy-json-schema';
// if easy-json-schema is not available, you can use the following import statement
// import easyJsonSchema from "https://cdn.skypack.dev/easy-json-schema@0.0.2-beta";
import { addSchemaToCollection, updateSchema } from "../../assets/constants/global_apis";
import { downloadCollectionSchemas } from '../../assets/constants/global_apis';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 350,
    bgcolor: 'background.paper',
    border: 'none',
    boxShadow: 24,
    pt: 2,
    paddingLeft: 0,
    paddingRight: 0,
};



const JsonEdit = ({ handleCloseSchema, schemaJsonList, setSchemaJsonList, getCollectionSchemaData, schemaEditorRef }) => {
    const [maxElements, setMaxElements] = useState(schemaJsonList.length ? schemaJsonList.length : 0);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const collectionId = useSelector(state => state.collections.collectionId);
    const [error, setError] = useState({
        type: '',
        message: ''
    });

    useEffect(() => {
        if (selectedIndex !== null && schemaJsonList[selectedIndex]?.schema) {
            const container = schemaEditorRef.current;
            const options = {
                mode: 'code',
                onBlur: () => {
                    try {
                        const updatedJson = editor?.get();
                        handleJsonChange(updatedJson);
                    } catch (error) {
                        console.log("Invalid JSON");
                    }
                },
                history: true
            };
            const editor = new JSONEditor(container, options);
            editor.set(schemaJsonList[selectedIndex].schema);

            if (document.querySelector(".jsoneditor-poweredBy")) {
                document.querySelector(".jsoneditor-poweredBy").remove();
            }
            if (document.querySelector(".jsoneditor-transform")) {
                document.querySelector(".jsoneditor-transform").remove();
            }
            if (document.querySelector(".jsoneditor-sort")) {
                document.querySelector(".jsoneditor-sort").remove();
            }
            if (document.querySelector(".jsoneditor-repair")) {
                document.querySelector(".jsoneditor-repair").remove();
            }
            return () => editor.destroy();
        }
    }, [selectedIndex, schemaJsonList]);

    const handleAddCollectionSchema = async () => {
        setSelectedIndex(0)
        const number = parseInt(maxElements);
        if (isNaN(number) || number < 0) {
            setError({ type: 'error', message: "Enter a valid number" });
            return;
        }
        addSchemaToCollection(number, { collection_id: collectionId })
            .then((response) => {
                setError({ type: "success", message: response.detail });
                getCollectionSchemaData(collectionId);
            }).catch((e) => {
                setError({ type: "error", message: `Cannot Create Schemas ${e?.message ? e?.message : "Schema counts are same"}` });
            })

    };
    const handleDownloadSchemas = () => {
        downloadCollectionSchemas(collectionId)
    }

    // Handle item click
    const handleItemClick = (index) => {
        setSelectedIndex(index);
    };

    // Update JSON data when edited
    const handleJsonChange = (updatedJson) => {
        const newJsonSchemaList = [...schemaJsonList];
        newJsonSchemaList[selectedIndex].schema = updatedJson;
        setSchemaJsonList(newJsonSchemaList);
    };

    const handleConvertToSchema = () => {
        setTimeout(() => {
            const json = schemaJsonList[selectedIndex].schema;
            if (typeof json !== "object" || Array.isArray(json)) {
                setError({ type: 'error', message: "Invalid JSON format" });
                return;
            }

            if (json.type && json.properties) {
                setError({ type: 'error', message: "JSON is already a schema!" });
                return;
            }
            const schema = EasyJSONSchema(json);
            const newSchemaJsonList = [...schemaJsonList];
            newSchemaJsonList[selectedIndex].schema = schema;
            setSchemaJsonList(newSchemaJsonList);
        }, 0)
    }

    const validateJsonSchema = (validatetype) => {
        try {
            const schema = schemaJsonList[selectedIndex].schema;
            const errors = validateSchemaStructure(schema);
            if (errors.length === 0) {
                if (!validatetype) {
                    setError({ type: 'success', message: "Valid Schema" });
                }
                return true;
            } else {
                setError({ type: 'error', 'message': errors.join(', ') });
                return false;
            }
        } catch (e) {
            setError({ type: 'error', 'message': "Invalid JSON format" });
            return false;
        }
    }

    const validateSchema = async (validatetype) => {
        setTimeout(() => {
            validateJsonSchema(validatetype);
        }, 0);
    };

    const validateSchemaStructure = (schema, path = "root") => {
        let errors = [];

        if (typeof schema !== "object" || schema === null) {
            errors.push(`${path}: Schema should be an object`);
            return errors;
        }

        // Check for "type" property
        if (!schema?.type) {
            errors.push(`${path}: Missing "type" property`);
        } else if (!["string", "number", "integer", "boolean", "array", "object"].includes(schema?.type)) {
            errors.push(`${path}: Invalid "type" value`);
        }

        // Validate properties if type is "object"
        if (schema?.type === "object") {
            if (!schema?.properties || typeof schema?.properties !== "object") {
                errors.push(`${path}: "properties" should be an object`);
            } else {
                for (let key in schema?.properties) {
                    errors = errors.concat(validateSchemaStructure(schema.properties[key], `${path}.properties.${key}`));
                }
            }
        }

        // Validate items if type is "array"
        if (schema?.type === "array") {
            if (!schema?.items) {
                errors.push(`${path}: Missing "items" definition for array`);
            } else {
                errors = errors.concat(validateSchemaStructure(schema.items, `${path}.items`));
            }
        }

        return errors;
    };


    // Save JSON to backend
    const handleSaveJson = async () => {
        setTimeout(() => {
            try {
                if (selectedIndex === null) {
                    setError({ type: 'error', message: "Select an item first" });
                    return;
                }
                let validSchema = validateJsonSchema(true);
                if (!validSchema) {
                    setError({ type: 'error', message: "Invalid schema!!!" });
                    return;
                }
                updateSchema({
                    collection_id: schemaJsonList[selectedIndex].collection_id,
                    schema_id: schemaJsonList[selectedIndex].schema_id,
                    schema: schemaJsonList[selectedIndex].schema,
                }).then(() => {
                    setError({ type: "success", message: "JSON saved successfully" });
                }).catch((e) => {
                    setError({ type: "error", message: 'Failed to save schema' })
                });
            } catch (error) {
                setError({ type: 'error', message: "Error saving JSON" });
            }
        }, 0);
    };


    useEffect(() => {
        if (error.type) {
            setTimeout(() => {
                setError({
                    type: '',
                    message: ''
                })
            }, 1000)
        }
    }, [error])

    return (
        <Box
            sx={{
                ...style,
                width: '80%',
                maxWidth: '900px',
                margin: 'auto',
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '20px',
                boxShadow: 24,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                marginBottom: '0px'
            }}
        >
            <div style={{ flexGrow: 1 }}>
                <Box className="json-edit-container">
                    {error?.type != '' && <div>
                        <div className="error-message-contaienr">
                            <span className={`alert-message ${error.type == 'error' ? 'error-message' : error.type == 'success' ? 'success-message' : error.type == 'info' && 'info-message'}`}>
                                {error?.message}
                            </span>
                        </div>
                    </div>}
                    <Box className="json-edit-body">
                        <Box className="json-edit-left-panel">
                            <Typography variant="h6" className="json-edit-title">
                                Schema Editor
                            </Typography>
                            <Typography variant="h6" className="json-edit-subtitle">
                                Number of Schemas
                            </Typography>
                            <Box className="json-edit-input-group">
                                <TextField
                                    size="small"
                                    type="number"
                                    className="schema-count-input"
                                    value={maxElements}
                                    onChange={(e) => setMaxElements(e.target.value >= 0 ? e.target.value : maxElements)}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleAddCollectionSchema}
                                    className="schema-count-button"
                                >
                                    Submit
                                </Button>
                            </Box>
                            <Typography variant="h6" className="json-edit-items-title">
                                <Typography>  Collection Schemas</Typography>
                                <Tooltip title={'Download Schemas'} placement="top" followCursor>
                                    <div><FontAwesomeIcon icon={faDownload} className="schema-downloader" onClick={handleDownloadSchemas} /></div>
                                </Tooltip>
                            </Typography>
                            {schemaJsonList.map((item, index) => (
                                <Box
                                    key={index}
                                    className={`json-edit-item ${selectedIndex === index ? "selected" : ""
                                        }`}
                                    onClick={() => handleItemClick(index)}
                                >
                                    schema {item.schema_id}
                                </Box>
                            ))}
                        </Box>

                        <Box className="json-edit-right-panel">

                            <Button
                                onClick={handleCloseSchema}
                                variant="contained"
                                className="schema-clsoe-button"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </Button>

                            <div ref={schemaEditorRef} className="json-editor-element"
                                style={{ display: selectedIndex !== null ? 'block' : 'none' }}
                            />
                            {selectedIndex === null && (
                                <Typography variant="body1" className="json-edit-placeholder">
                                    Select an item to edit JSON
                                </Typography>
                            )}

                            <Box className="json-edit-buttons">
                                <Button
                                    variant="contained"
                                    className="json-edit-button save-button"
                                    onClick={handleSaveJson}
                                >
                                    Save
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className="json-edit-button"
                                    onClick={handleConvertToSchema}
                                >
                                    Convert to Schema
                                </Button>
                                <Button
                                    variant="contained"
                                    className="json-edit-button validate-button"
                                    onClick={() => validateSchema(false)}
                                >
                                    Validate
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </div>
        </Box>
    );
};

export default JsonEdit;
